/* ───────────────────────────────────────────────────────────────────
   Map.js  – full file with working Sign‑out + LayersControl tweaks
   ─────────────────────────────────────────────────────────────────── */
   import { useState, useContext, useEffect } from "react";
   import {
     MapContainer,
     TileLayer,
     LayersControl,
     Marker,
     ZoomControl,
   } from "react-leaflet";
   import "leaflet/dist/leaflet.css";
   import "./map.css";
   
   import LocateControl from "./components/LocateControl";
   import WMSLayers from "./components/WMSLayers";
   import WMSData from "./components/WMSData";
   import LayerSwitch from "./components/LayerSwitch";
   import MarkerIcon from "./components/MarkerIcon";
   import LayerLegends from "./components/Legends/LayerLegends";
   import InfoPanel from "./components/infopanel/InfoPanel";
   import ElasticSearch from "./components/elasticSearch/ElasticSearch";
   
   import { Link } from "react-router-dom";
   import { Dropdown } from "react-bootstrap";
   import { AuthContext } from "../../context/AuthContext";
   
   import ApiAuth from "../../API/ApiAuth";           // ⬅️  keep .signout
   import { removeTokenUser } from "../../Redux/variables";
   import Auth from "../../Redux/Actions/Auth";
   
   import { toast } from "react-toastify";
   import { useDispatch, useSelector } from "react-redux";
   
   import TagManager from "react-gtm-module";
   import { GoogleView } from "../../Helper/GoogleView";
   import { Helmet } from "react-helmet";
   import { getPageTitle } from "../../Redux/variables";
   import { isMobile } from "../../utils";
   
   /* 🔸 LayersControl custom CSS – click to open, no hover, panel lower */
   const customLayerStyle = `
     .leaflet-control-layers { cursor: pointer; }
   
     /* hide panel by default + push lower */
     .leaflet-control-layers .leaflet-control-layers-list {
       display: none;
       margin-top: -24px;
     }
   
     /* force hide on hover (override Leaflet default) */
     .leaflet-control-layers:not(.leaflet-control-layers-expanded):hover
       .leaflet-control-layers-list {
       display: none !important;
     }
   
     /* show panel only after click (expanded class) */
     .leaflet-control-layers.leaflet-control-layers-expanded
       .leaflet-control-layers-list {
       display: block !important;
     }
   
     /* keep toggle icon top of list */
     .leaflet-control-layers-toggle { z-index: 1001; }
   `;
   
   function Map(props) {
     const dispatch = useDispatch();
     const { currentUser, setCurrentUser } = useContext(AuthContext);
     const loader = useSelector((state) => state.user.loader);
   
     /* ───────────── Side‑effects: analytics + auth redirect ───────────── */
     useEffect(() => {
       GoogleView("Prop Map", "/map");
     }, []);
   
     useEffect(() => {
       if (currentUser) {
         TagManager.dataLayer({
           dataLayer: {
             userId: currentUser.id,
             userProject: "Users",
             page: "map",
           },
           dataLayerName: "PageDataLayer",
         });
       }
     }, [currentUser]);
   
     useEffect(() => {
       if (!currentUser) props.history.push("/");
     }, [currentUser, props.history]);
   
     /* ───────────── Map / UI state ───────────── */
     const [mapzoom, setmapzoom] = useState(6);
     const [comingSoonData, setComingSoonData] = useState({});
     const [maphook, setMaphook] = useState(null);
     const [plotfilter, setplotfilter] = useState("all");
     const [centroid, setcentroid] = useState();
     const [plotData, setplotData] = useState("");
     const [infoPanelState, setInfoPanelState] = useState({
       left: false,
     });
   
     /* ───────────── Sign‑out handler (uses ApiAuth.signout) ───────────── */
     const signout = () => {
       ApiAuth.signout({})
         .then((response) => {
           removeTokenUser();
           dispatch(Auth.signout({}));
           toast.success(response.message);
           props.history.push("/");
           setCurrentUser(null);
         })
         .catch((err) => {
           toast.error(err.message || "Logout error");
           removeTokenUser();
           window.location.href = "/";
         });
     };
   
     /* ───────────── Render ───────────── */
     return (
       <>
         <Helmet>
           <title>NAB Digital Map {getPageTitle()}</title>
           <meta name="description" content="" />
         </Helmet>
   
         {mapzoom >= 17 && (
           <LayerSwitch setplotfilter={setplotfilter} plotfilter={plotfilter} />
         )}
   
         {centroid && !loader && (
           <InfoPanel
             state={infoPanelState}
             setState={setInfoPanelState}
             plotData={plotData}
             user={currentUser}
           />
         )}
   
         <ElasticSearch
           user={currentUser}
           setcentroid={setcentroid}
           map={maphook}
           open={infoPanelState.left}
           plotData={plotData}
           setplotData={setplotData}
         />
   
         {mapzoom && <LayerLegends mapzoom={mapzoom} />}
   
         {/*  🌍  MAP  */}
         <MapContainer
           center={[33.68895201115458, 73.11999708087887]}
           minZoom={10}
           maxZoom={22}
           zoom={11}
           zoomControl={false}
           whenCreated={(map) => {
             setMaphook(map);
             map.on("zoomend", () => setmapzoom(map.getZoom()));
             map.on("click", (e) => setcentroid(e.latlng));
           }}
         >
           <style>{customLayerStyle}</style>
   
           {mapzoom > 16 && centroid && (
             <>
               <Marker position={centroid} icon={MarkerIcon} />
               <WMSData
                 map={maphook}
                 centroid={centroid}
                 setState={setInfoPanelState}
                 setplotData={setplotData}
               />
             </>
           )}
   
           {/* 🗺️ Base‑layer switcher (icon click → dropdown) */}
           <LayersControl position="topright" style={{height:"4px"}}>
             <LayersControl.BaseLayer  name="Arch GIS Map">
               <TileLayer
                 attribution="mapbox"
                 url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                 maxZoom={22}
               />
             </LayersControl.BaseLayer>
               <LayersControl.BaseLayer checked name="Street View">
               <TileLayer
                 attribution="mapbox"
                 url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                 maxZoom={22}
               />
             </LayersControl.BaseLayer>

   
             <LayersControl.BaseLayer name="Gray Canvas">
               <TileLayer
                 attribution="openstreetmaps"
                 url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                 maxZoom={22}
               />
             </LayersControl.BaseLayer>
   
             <LayersControl.BaseLayer name="Satellite View">
               <TileLayer
                 attribution="googlemaps"
                 url="https://mt0.google.com/vt/lyrs=y&hl=en&x={x}&y={y}&z={z}"
                 maxZoom={22}
               />
             </LayersControl.BaseLayer>
           </LayersControl>
   
           {/* 📋  App menu (top‑right) */}
           <div
             style={{
               marginTop: isMobile() ? "80px" : "15px",
               width: "330px",
               position: "fixed",
               right: isMobile() ? "45px" : "70px",
               zIndex: 900,
             }}
           >
             <Dropdown>
               <Dropdown.Toggle
                 className="btn btn-light text-dark drop-shadow map-menu-dropdown-toggle"
                 variant=""
                 style={{ width: "330px", textAlignLast: "start" }}
               >
                 Menu
               </Dropdown.Toggle>
               <Dropdown.Menu style={{ width: "330px" }}>
                 <Dropdown.Item as={Link} to="/profile" style={{color: "#003366",}}>
                   Profile
                 </Dropdown.Item>
                 <Dropdown.Item as={Link} to="/request-history" style={{color: "#003366",}}>
                   Request History
                 </Dropdown.Item>
                 <Dropdown.Divider />
                 <Dropdown.Item onClick={signout} style={{color: "#003366",}}>Sign out</Dropdown.Item>
               </Dropdown.Menu>
             </Dropdown>
           </div>
   
           <ZoomControl position="bottomright" />
           <LocateControl />
           <WMSLayers mapzoom={mapzoom} plotfilter={plotfilter} />
         </MapContainer>
       </>
     );
   }
   
   export default Map;
   